import LabTopologyDiagram from "./LabTopologyDiagram";
import LabValidationPanel from './LabValidationPanel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../axiosConfig";

const TUTOR_MODE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "explain_simple", label: "Explain simply" },
  { value: "explain_deep", label: "Explain deeply" },
  { value: "example", label: "Another example" },
  { value: "packet_flow", label: "Packet flow" },
  { value: "compare", label: "Compare technologies" },
  { value: "junos_configuration", label: "Junos configuration" },
  { value: "troubleshooting", label: "Troubleshooting" },
  { value: "quiz", label: "Quiz me" },
  { value: "configuration_review", label: "Review my configuration" },
  { value: "command_output", label: "Explain command output" },
];

const SUGGESTED_PROMPTS = [
  { label: "Explain simply", text: "Explain this more simply." },
  { label: "Explain deeply", text: "Explain this in more depth." },
  { label: "Show packet flow", text: "Show the packet flow step by step." },
  { label: "Give another example", text: "Give me another example." },
  { label: "Show Junos configuration", text: "Show a Junos configuration example for this lesson." },
  { label: "Compare technologies", text: "Compare this with a related protocol or technology." },
  { label: "Create troubleshooting scenario", text: "Create a troubleshooting scenario for this lesson." },
  { label: "Quiz me", text: "Quiz me on this lesson." },
  { label: "Review my configuration", text: "Review my configuration and explain likely issues." },
  { label: "Explain command output", text: "Explain this command output." },
];

function isPmpLesson(item, lesson) {
  const certification = String(item?.certification || lesson?.certification || "").trim().toUpperCase();
  const examCode = String(item?.examCode || lesson?.examCode || "").trim().toUpperCase();
  return certification === "PMP" || examCode === "PMP";
}

function buildTutorModeOptions(pmpLesson) {
  if (!pmpLesson) return TUTOR_MODE_OPTIONS;
  return [
    { value: "general", label: "General" },
    { value: "explain_simple", label: "Explain simply" },
    { value: "explain_deep", label: "Explain deeply" },
    { value: "example", label: "Another example" },
    { value: "packet_flow", label: "Decision flow" },
    { value: "compare", label: "Compare approaches" },
    { value: "junos_configuration", label: "Show artifacts" },
    { value: "troubleshooting", label: "Scenario coaching" },
    { value: "quiz", label: "Quiz me" },
    { value: "configuration_review", label: "Review my reasoning" },
    { value: "command_output", label: "Explain answer choices" },
  ];
}

function buildSuggestedPrompts(pmpLesson, lesson) {
  if (Array.isArray(lesson?.suggestedTutorPrompts) && lesson.suggestedTutorPrompts.length) {
    return lesson.suggestedTutorPrompts
      .map((prompt) => ({ label: String(prompt || "").trim(), text: String(prompt || "").trim() }))
      .filter((prompt) => prompt.label);
  }

  if (!pmpLesson) return SUGGESTED_PROMPTS;

  return [
    { label: "Explain simply", text: "Explain this in simpler terms." },
    { label: "Explain deeply", text: "Explain this in more depth." },
    { label: "Decision flow", text: "Walk me through the decision step by step." },
    { label: "Another example", text: "Give me another project example." },
    { label: "Show artifacts", text: "Show me the relevant artifact or plan for this lesson." },
    { label: "Compare approaches", text: "Compare predictive, agile, and hybrid handling for this topic." },
    { label: "Scenario coaching", text: "Create a scenario question for this lesson and coach me through it." },
    { label: "Quiz me", text: "Quiz me on this lesson." },
    { label: "Review my reasoning", text: "Review my reasoning and explain where it is weak." },
    { label: "Explain answer choices", text: "Explain why the other answer choices are weaker." },
  ];
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatMinutes(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "Not set";
  return `${minutes} min`;
}

function labelizeMode(value) {
  const match = TUTOR_MODE_OPTIONS.find((item) => item.value === value);
  return match?.label || String(value || "General").replace(/_/g, " ");
}

function CollapsibleSection({ title, children, defaultExpanded = true, action = null }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  return (
    <section className="learning-reader-section" style={title === "Lab exercises" ? {gridColumn:"1 / -1",minWidth:0} : undefined}>
      <div className="learning-reader-section-top">
        <h3>{title}</h3>
        <div className="learning-reader-section-actions">
          {action}
          <button type="button" className="action-link" onClick={() => setIsExpanded((current) => !current)}>
            {isExpanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>
      {isExpanded ? <div className="learning-reader-section-body">{children}</div> : null}
    </section>
  );
}

function renderList(items) {
  return (
    <ul className="learning-reader-list">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

function normalizeQuestionType(type) {
  const value = String(type || "").trim();
  return value === "multiple_select" ? "multiple" : "single";
}

function TutorMessage({ message }) {
  return (
    <article className={`learning-tutor-message is-${message.role}`}>
      <div className="learning-tutor-message-top">
        <strong>{message.role === "assistant" ? "AI Tutor" : "You"}</strong>
        <span>{labelizeMode(message.mode)}</span>
      </div>
      <div className="learning-tutor-message-body">
        {String(message.content || "").split("\n").map((line, index) => (
          <p key={`${message.id}-${index}`}>{line || "\u00a0"}</p>
        ))}
      </div>
      <div className="learning-tutor-message-meta">
        <span>{formatDateTime(message.createdAt)}</span>
        {message.status === "failed" ? <span className="learning-tutor-message-status error">Failed</span> : null}
        {message.status === "pending" ? <span className="learning-tutor-message-status">Thinking...</span> : null}
      </div>
      {message.status === "failed" && message.errorMessage ? (
        <div className="learning-tutor-inline-error">{message.errorMessage}</div>
      ) : null}
    </article>
  );
}

export default function LearningLessonView({ itemId, onBack, onGoHome }) {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [item, setItem] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [quizSelections, setQuizSelections] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [showLabAnswerKey, setShowLabAnswerKey] = useState(false);

  const [tutorLoading, setTutorLoading] = useState(true);
  const [tutorError, setTutorError] = useState("");
  const [tutorNotice, setTutorNotice] = useState("");
  const [tutorConversations, setTutorConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState("");
  const [activeConversation, setActiveConversation] = useState(null);
  const [tutorMessages, setTutorMessages] = useState([]);
  const [tutorInput, setTutorInput] = useState("");
  const [tutorMode, setTutorMode] = useState("general");
  const [tutorSending, setTutorSending] = useState(false);
  const [tutorCreating, setTutorCreating] = useState(false);
  const [tutorClearing, setTutorClearing] = useState(false);
  const [tutorDeleting, setTutorDeleting] = useState(false);
  const tutorEndRef = useRef(null);
  const tutorInputRef = useRef(null);

  const loadLesson = useCallback(async ({ autoStart = true } = {}) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await api.get(`/api/learning/study-items/${itemId}/lesson`);
      let nextItem = response.data?.item || null;
      let nextLesson = response.data?.lesson || null;
      const nextAttempts = Array.isArray(response.data?.quizAttempts) ? response.data.quizAttempts : [];

      if (autoStart && nextItem?.status === "ready") {
        const startResponse = await api.post(`/api/learning/study-items/${itemId}/start`);
        nextItem = startResponse.data?.item || nextItem;
        nextLesson = startResponse.data?.lesson || nextLesson;
      }

      setItem(nextItem);
      setLesson(nextLesson);
      setQuizAttempts(nextAttempts);
      setQuizResult(null);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Could not load lesson.");
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  const loadTutorConversations = useCallback(async () => {
    setTutorLoading(true);
    setTutorError("");
    try {
      const response = await api.get(`/api/learning/lessons/${itemId}/tutor/conversations`);
      const conversations = Array.isArray(response.data?.conversations) ? response.data.conversations : [];
      setTutorConversations(conversations);
      setActiveConversationId((current) => {
        if (current && conversations.some((entry) => entry.id === current)) {
          return current;
        }
        return conversations[0]?.id || "";
      });
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not load AI Tutor conversations.");
    } finally {
      setTutorLoading(false);
    }
  }, [itemId]);

  const loadTutorConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setActiveConversation(null);
      setTutorMessages([]);
      return;
    }

    setTutorError("");
    try {
      const response = await api.get(`/api/learning/lessons/${itemId}/tutor/conversations/${conversationId}`);
      setActiveConversation(response.data?.conversation || null);
      setTutorMessages(Array.isArray(response.data?.messages) ? response.data.messages : []);
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not load this AI Tutor conversation.");
    }
  }, [itemId]);

  useEffect(() => {
    loadLesson();
    loadTutorConversations();
  }, [loadLesson, loadTutorConversations]);

  useEffect(() => {
    loadTutorConversation(activeConversationId);
  }, [activeConversationId, loadTutorConversation]);

  useEffect(() => {
    tutorEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [tutorMessages, activeConversationId]);

  const quizQuestions = useMemo(() => Array.isArray(lesson?.quiz) ? lesson.quiz : [], [lesson]);
  const isLab = item?.itemType === "lab";
  const pmpLesson = useMemo(() => isPmpLesson(item, lesson), [item, lesson]);
  const tutorModeOptions = useMemo(() => buildTutorModeOptions(pmpLesson), [pmpLesson]);
  const suggestedPrompts = useMemo(() => buildSuggestedPrompts(pmpLesson, lesson), [pmpLesson, lesson]);
  const pythonLesson = lesson?.language === "python" || item?.trackId === "python-general";
  const technicalExplanationTitle = pmpLesson ? "Decision framing" : "Technical explanation";
  const controlPlaneTitle = pythonLesson ? "Program design" : pmpLesson ? "Planning perspective" : "Control-plane explanation";
  const dataPlaneTitle = pythonLesson ? "Execution and output" : pmpLesson ? "Execution perspective" : "Data-plane explanation";
  const packetFlowTitle = pythonLesson ? "Execution steps" : pmpLesson ? "Decision flow" : "Packet flow";
  const configurationSectionTitle = pythonLesson ? "Python example" : pmpLesson ? "Artifact guidance" : "Junos configuration";
  const configurationActionLabel = pythonLesson ? "Copy code" : pmpLesson ? "Copy guidance" : "Copy commands";
  const verificationSectionTitle = pythonLesson ? "Run and verify" : pmpLesson ? "Verification guidance" : "Verification commands";

  const latestAttempt = quizResult || quizAttempts.find((attempt) => attempt.generationVersion == null || attempt.generationVersion === item?.generationVersion) || null;
  const historicalAttempt = quizAttempts.find((attempt) => attempt.generationVersion != null && attempt.generationVersion !== item?.generationVersion);
  const latestFailedAssistantMessage = [...tutorMessages].reverse().find((message) => message.role === "assistant" && message.status === "failed") || null;
  const latestUserPrompt = latestFailedAssistantMessage
    ? [...tutorMessages]
        .slice(0, tutorMessages.findIndex((entry) => entry.id === latestFailedAssistantMessage.id))
        .reverse()
        .find((entry) => entry.role === "user")
    : null;

  const handleCopy = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallbackArea = document.createElement("textarea");
        fallbackArea.value = text;
        document.body.appendChild(fallbackArea);
        fallbackArea.select();
        document.execCommand("copy");
        fallbackArea.remove();
      }
      setCopyMessage("Copied");
      window.setTimeout(() => setCopyMessage(""), 1400);
    } catch {
      setCopyMessage("Copy failed");
      window.setTimeout(() => setCopyMessage(""), 1400);
    }
  };

  const updateQuizSelection = (questionId, optionId, mode) => {
    setQuizSelections((current) => {
      const existing = Array.isArray(current[questionId]) ? current[questionId] : [];
      if (mode === "multiple") {
        const next = existing.includes(optionId)
          ? existing.filter((value) => value !== optionId)
          : [...existing, optionId];
        return { ...current, [questionId]: next };
      }
      return { ...current, [questionId]: [optionId] };
    });
  };

  const handleQuizSubmit = async () => {
    setQuizSubmitting(true);
    setErrorMessage("");
    try {
      const answers = quizQuestions.map((question) => ({
        questionId: question.id,
        selectedOptionIds: Array.isArray(quizSelections[question.id]) ? quizSelections[question.id] : [],
      }));
      const response = await api.post(`/api/learning/study-items/${itemId}/quiz-attempts`, { answers });
      if (response.data?.attempt) {
        setQuizResult(response.data.attempt);
        setQuizAttempts((current) => [response.data.attempt, ...current]);
      }
      if (response.data?.item) {
        setItem(response.data.item);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Could not submit quiz.");
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    setErrorMessage("");
    try {
      const response = await api.post(`/api/learning/study-items/${itemId}/complete`, { completed: item.status !== "completed" });
      if (response.data?.item) {
        setItem(response.data.item);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Could not update completion status.");
    } finally {
      setCompleting(false);
    }
  };

  const createTutorConversation = useCallback(async () => {
    setTutorCreating(true);
    setTutorError("");
    setTutorNotice("");
    try {
      const response = await api.post(`/api/learning/lessons/${itemId}/tutor/conversations`, {});
      const conversation = response.data?.conversation || null;
      if (conversation) {
        setTutorConversations((current) => [conversation, ...current]);
        setActiveConversationId(conversation.id);
        setActiveConversation(conversation);
        setTutorMessages(Array.isArray(response.data?.messages) ? response.data.messages : []);
        setTutorNotice("Conversation created. Ask your first question below.");
        window.setTimeout(() => {
          tutorInputRef.current?.focus();
        }, 0);
      }
      return conversation;
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not create a new AI Tutor conversation.");
      return null;
    } finally {
      setTutorCreating(false);
    }
  }, [itemId]);

  const ensureTutorConversation = useCallback(async () => {
    if (activeConversationId) return activeConversationId;
    const created = await createTutorConversation();
    return created?.id || "";
  }, [activeConversationId, createTutorConversation]);

  const sendTutorMessage = useCallback(async (messageText, { clearInput = true } = {}) => {
    const content = String(messageText || "").trim();
    if (!content) return;

    setTutorSending(true);
    setTutorError("");
    setTutorNotice("");
    try {
      const conversationId = await ensureTutorConversation();
      if (!conversationId) return;

      const response = await api.post(
        `/api/learning/lessons/${itemId}/tutor/conversations/${conversationId}/messages`,
        {
          content,
          mode: tutorMode,
        }
      );
      const nextConversation = response.data?.conversation || null;
      const nextMessages = Array.isArray(response.data?.messages) ? response.data.messages : [];

      if (nextConversation) {
        setActiveConversation(nextConversation);
        setTutorConversations((current) => {
          const rest = current.filter((entry) => entry.id !== nextConversation.id);
          return [nextConversation, ...rest];
        });
      }
      setTutorMessages(nextMessages);
      if (clearInput) {
        setTutorInput("");
      }
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not send message to AI Tutor.");
    } finally {
      setTutorSending(false);
    }
  }, [ensureTutorConversation, itemId, tutorMode]);

  const handleTutorSubmit = async (event) => {
    event.preventDefault();
    await sendTutorMessage(tutorInput, { clearInput: true });
  };

  const handleRetryFailedMessage = async () => {
    if (!latestUserPrompt?.content || tutorSending) return;
    await sendTutorMessage(latestUserPrompt.content, { clearInput: false });
  };

  const handleClearConversation = async () => {
    if (!activeConversationId || tutorClearing) return;
    setTutorClearing(true);
    setTutorError("");
    setTutorNotice("");
    try {
      const response = await api.post(`/api/learning/lessons/${itemId}/tutor/conversations/${activeConversationId}/clear`);
      const nextConversation = response.data?.conversation || null;
      setTutorMessages([]);
      if (nextConversation) {
        setActiveConversation(nextConversation);
        setTutorConversations((current) => current.map((entry) => (entry.id === nextConversation.id ? nextConversation : entry)));
      }
      setTutorNotice("Conversation cleared.");
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not clear this AI Tutor conversation.");
    } finally {
      setTutorClearing(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeConversationId || tutorDeleting) return;
    if (!window.confirm("Delete this AI Tutor conversation?")) return;
    setTutorDeleting(true);
    setTutorError("");
    setTutorNotice("");
    try {
      await api.delete(`/api/learning/lessons/${itemId}/tutor/conversations/${activeConversationId}`);
      const nextConversations = tutorConversations.filter((entry) => entry.id !== activeConversationId);
      setTutorConversations(nextConversations);
      setActiveConversationId(nextConversations[0]?.id || "");
      setActiveConversation(nextConversations[0] || null);
      setTutorMessages(nextConversations[0] ? tutorMessages : []);
      if (!nextConversations.length) {
        setTutorMessages([]);
      }
      setTutorNotice("Conversation deleted.");
    } catch (error) {
      setTutorError(error.response?.data?.error || "Could not delete this AI Tutor conversation.");
    } finally {
      setTutorDeleting(false);
    }
  };

  if (loading) {
    return <section className="learning-reader-shell"><div className="feedback-panel">Loading lesson...</div></section>;
  }

  if (errorMessage && !lesson) {
    return (
      <section className="learning-reader-shell">
        <div className="feedback-panel error">{errorMessage}</div>
        <div className="dashboard-actions">
          <button type="button" className="action-button" onClick={() => loadLesson()}>
            Retry
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={onBack}>
            Back to learning
          </button>
        </div>
      </section>
    );
  }

  if (!lesson || !item) {
    return (
      <section className="learning-reader-shell">
        <div className="learning-empty-state">
          <strong>No lesson content is available yet.</strong>
          <span>Generate a lesson from the study-item list before opening the lesson reader.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="learning-reader-shell">
      <div className="learning-reader-header">
        <div>
          <span className="eyebrow">{isLab ? "Lab Reader" : "Lesson Reader"}</span>
          <h2>{lesson.title}</h2>
          <div className="learning-track-meta">
            <span>{item.certification}</span>
            <span>{item.examCode}</span>
            <span>{item.domainTitle || "Track-wide"}</span>
            <span>{item.topicTitle || "General objective"}</span>
            <span>{item.status.replace(/_/g, " ")}</span>
            <span>{formatMinutes(lesson.estimatedMinutes)}</span>
          </div>
        </div>
        <div className="learning-reader-header-actions">
          <button type="button" className="action-button action-button-secondary" onClick={onBack}>
            Back to learning
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={onGoHome}>
            Dashboard
          </button>
          <button type="button" className="action-button" onClick={handleComplete} disabled={completing} aria-pressed={item.status === "completed"} title={item.status === "completed" ? "Mark as in progress" : "Mark complete"}>
            {completing ? "Saving..." : item.status === "completed" ? "Completed" : "Mark complete"}
          </button>
        </div>
      </div>

      {errorMessage ? <div className="feedback-panel error">{errorMessage}</div> : null}
      {copyMessage ? <div className="feedback-panel">{copyMessage}</div> : null}

      {lesson?.examAlignment ? <section className="learning-panel"><h3>Exam alignment</h3><p>{lesson.examAlignment.certification} · {lesson.examAlignment.examCode} · {lesson.examAlignment.referenceVersion}</p><p>Technical review needed · Router testing is recorded separately.</p><ul>{lesson.examAlignment.objectiveCoverage.map(o=><li key={o.id}><strong>{o.status}</strong> — {o.evidence}</li>)}</ul>{lesson.examAlignment.reviewWarnings?.length ? <ul>{lesson.examAlignment.reviewWarnings.map((w,i)=><li key={i}>{w}</li>)}</ul> : null}<p>{lesson.examAlignment.sources.map(source=><a key={source.url} href={source.url} target="_blank" rel="noreferrer" style={{marginRight:'1rem'}}>{source.title}</a>)}</p></section> : null}
      {isLab && lesson?.lab ? <LabValidationPanel itemId={item.id} version={item.generationVersion} lab={lesson.lab} /> : null}
      <div className="learning-lesson-layout">
        <div className="learning-lesson-main">
          <section className="learning-panel learning-reader-meta">
            <div className="learning-item-meta">
              <span>Created {formatDateTime(item.createdAt)}</span>
              <span>Last studied {formatDateTime(item.lastStudiedAt)}</span>
              <span>Started {formatDateTime(item.startedAt)}</span>
              <span>Completed {formatDateTime(item.completedAt)}</span>
              <span>Next review {formatDateTime(item.nextReviewAt)}</span>
            </div>
          </section>

          <div className="learning-reader-grid">
            <CollapsibleSection title="Learning objectives">
              {renderList(lesson.learningObjectives || [])}
            </CollapsibleSection>

            <CollapsibleSection title="Prerequisite review">
              {renderList(lesson.prerequisiteReview || [])}
            </CollapsibleSection>

            <CollapsibleSection title={isLab ? "Lab exercises" : "Lab"} defaultExpanded={isLab}>
              {lesson.lab ? (
                <>
                  <p><strong>{lesson.lab.title}</strong> · {formatMinutes(lesson.lab.estimatedMinutes)}</p>
                  <LabTopologyDiagram topology={lesson.lab.topologyDiagram} />
                  <p>{lesson.lab.topology}</p>
                  <p><strong>Devices</strong></p>
                  {renderList(lesson.lab.devices || [])}
                  <p><strong>Addressing</strong></p>
                  {renderList(lesson.lab.addressing || [])}
                  <p><strong>Tasks</strong></p>
                  {renderList(lesson.lab.tasks || [])}
                  <p><strong>Verification</strong></p>
                  {renderList(lesson.lab.verificationCommands || [])}
                  <p><strong>Hints</strong></p>
                  {renderList(lesson.lab.hints || [])}
                  <button type="button" className="action-link" onClick={() => setShowLabAnswerKey((current) => !current)}>
                    {showLabAnswerKey ? "Hide answer key" : "Show answer key"}
                  </button>
                  {showLabAnswerKey ? renderList(lesson.lab.answerKey || []) : null}
                </>
              ) : (
                <p>No lab was generated for this lesson.</p>
              )}
            </CollapsibleSection>

            <CollapsibleSection title="Overview">
              <p>{lesson.overview}</p>
            </CollapsibleSection>

            <CollapsibleSection title={technicalExplanationTitle}>
              <p>{lesson.technicalExplanation}</p>
            </CollapsibleSection>

            <CollapsibleSection title={controlPlaneTitle}>
              <p>{lesson.controlPlaneExplanation}</p>
            </CollapsibleSection>

            <CollapsibleSection title={dataPlaneTitle}>
              <p>{lesson.dataPlaneExplanation}</p>
            </CollapsibleSection>

            <CollapsibleSection title={packetFlowTitle}>
              {renderList(lesson.packetFlow || [])}
            </CollapsibleSection>

            <CollapsibleSection title={configurationSectionTitle} action={lesson?.junosConfiguration?.commands?.length ? (
              <button
                type="button"
                className="action-link"
                onClick={() => handleCopy((lesson.junosConfiguration.commands || []).join("\n"))}
              >
                {configurationActionLabel}
              </button>
            ) : null}>
              <p><strong>Platform:</strong> {lesson.junosConfiguration?.platform || "Not specified"}</p>
              <p><strong>Configuration type:</strong> {lesson.junosConfiguration?.configurationType || "excerpt"}</p>
              {renderList(lesson.junosConfiguration?.assumptions || [])}
              <pre className="learning-code-block"><code>{(lesson.junosConfiguration?.commands || []).join("\n")}</code></pre>
              {renderList(lesson.junosConfiguration?.explanation || [])}
            </CollapsibleSection>

            <CollapsibleSection title={verificationSectionTitle}>
              <div className="learning-verification-list">
                {(lesson.verification || []).map((entry) => (
                  <article key={`${entry.command}-${entry.purpose}`} className="learning-verification-card">
                    <div className="learning-verification-top">
                      <strong>{entry.command}</strong>
                      <button type="button" className="action-link" onClick={() => handleCopy(entry.command)}>
                        Copy
                      </button>
                    </div>
                    <p>{entry.purpose}</p>
                    {renderList(entry.importantFields || [])}
                    <p><strong>Expected:</strong> {entry.expectedResult}</p>
                  </article>
                ))}
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Common mistakes">
              <div className="learning-mistake-list">
                {(lesson.commonMistakes || []).map((entry) => (
                  <article key={`${entry.mistake}-${entry.symptom}`} className="learning-mistake-card">
                    <strong>{entry.mistake}</strong>
                    <p><strong>Symptom:</strong> {entry.symptom}</p>
                    <p><strong>Correction:</strong> {entry.correction}</p>
                  </article>
                ))}
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Troubleshooting scenario">
              <p>{lesson.troubleshootingScenario?.scenario}</p>
              <p><strong>Symptoms</strong></p>
              {renderList(lesson.troubleshootingScenario?.symptoms || [])}
              <p><strong>Questions</strong></p>
              {renderList(lesson.troubleshootingScenario?.questions || [])}
              <p><strong>Recommended workflow</strong></p>
              {renderList(lesson.troubleshootingScenario?.recommendedWorkflow || [])}
              <p><strong>Resolution:</strong> {lesson.troubleshootingScenario?.resolution}</p>
            </CollapsibleSection>

            <CollapsibleSection title="Quiz" defaultExpanded={false}>
              <div className="learning-quiz-list">
                {quizQuestions.map((question) => {
                  const mode = normalizeQuestionType(question.type);
                  const currentSelection = Array.isArray(quizSelections[question.id]) ? quizSelections[question.id] : [];
                  const gradedResult = latestAttempt?.responses?.find((entry) => entry.questionId === question.id) || null;
                  return (
                    <article key={question.id} className="learning-quiz-card">
                      <strong>{question.question}</strong>
                      <div className="learning-quiz-options">
                        {(question.options || []).map((option) => {
                          const isSelected = currentSelection.includes(option.id);
                          const isCorrect = gradedResult?.correctOptionIds?.includes(option.id);
                          const isSubmittedWrong = Boolean(gradedResult) && isSelected && !isCorrect;
                          return (
                            <label
                              key={option.id}
                              className={`learning-quiz-option${isCorrect ? " is-correct" : ""}${isSubmittedWrong ? " is-wrong" : ""}`}
                            >
                              <input
                                type={mode === "multiple" ? "checkbox" : "radio"}
                                name={question.id}
                                checked={isSelected}
                                onChange={() => updateQuizSelection(question.id, option.id, mode)}
                                disabled={quizSubmitting || Boolean(latestAttempt)}
                              />
                              <span>{option.text}</span>
                            </label>
                          );
                        })}
                      </div>
                      {gradedResult ? <p className="learning-quiz-explanation">{gradedResult.explanation}</p> : null}
                    </article>
                  );
                })}
              </div>
              <div className="dashboard-actions">
                <button type="button" className="action-button" onClick={handleQuizSubmit} disabled={quizSubmitting || Boolean(latestAttempt)}>
                  {quizSubmitting ? "Submitting..." : latestAttempt ? "Quiz submitted" : "Submit quiz"}
                </button>
              </div>
              {latestAttempt ? (
                <div className={`feedback-panel${latestAttempt.recommendedReview ? " error" : ""}`}>
                  Score: {latestAttempt.scorePercent}% ({latestAttempt.correctAnswers}/{latestAttempt.totalQuestions})
                  {latestAttempt.recommendedReview ? " Review is recommended before marking this topic done." : ""}
                </div>
              ) : null}
            </CollapsibleSection>

            {historicalAttempt ? <p className="learning-muted">Previous content version {historicalAttempt.generationVersion}: {historicalAttempt.scorePercent}% score. Your attempt history is preserved; this quiz uses the current content.</p> : null}
            <CollapsibleSection title="Flashcards" defaultExpanded={false}>
              <div className="learning-flashcard-grid">
                {(lesson.flashcards || []).map((card) => (
                  <article key={`${card.front}-${card.back}`} className="learning-flashcard">
                    <strong>{card.front}</strong>
                    <p>{card.back}</p>
                  </article>
                ))}
              </div>
            </CollapsibleSection>


            <CollapsibleSection title="Summary">
              {renderList(lesson.summary || [])}
              <p><strong>Recommended next topic:</strong> {lesson.nextRecommendedTopic || "Continue to the next saved study item."}</p>
            </CollapsibleSection>
          </div>
        </div>

        <aside className="learning-tutor-panel">
          <div className="learning-tutor-panel-top">
            <div>
              <span className="eyebrow">AI Tutor</span>
              <h3>{activeConversation?.title || "Lesson tutor"}</h3>
              <p>Ask follow-up questions without changing the saved lesson.</p>
            </div>
            <div className="learning-tutor-header-actions">
              <button type="button" className="action-button action-button-secondary" onClick={createTutorConversation} disabled={tutorCreating}>
                {tutorCreating ? "Creating..." : "New conversation"}
              </button>
              <button type="button" className="action-link" onClick={handleClearConversation} disabled={!activeConversationId || tutorClearing}>
                {tutorClearing ? "Clearing..." : "Clear"}
              </button>
              <button type="button" className="action-link danger" onClick={handleDeleteConversation} disabled={!activeConversationId || tutorDeleting}>
                {tutorDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>

          <div className="learning-tutor-controls">
            <label className="field-label">
              <span>Conversation</span>
              <select
                className="form-control"
                value={activeConversationId}
                onChange={(event) => setActiveConversationId(event.target.value)}
                disabled={tutorLoading || !tutorConversations.length}
              >
                <option value="">{tutorConversations.length ? "Select a conversation" : "No conversations yet"}</option>
                {tutorConversations.map((conversation) => (
                  <option key={conversation.id} value={conversation.id}>
                    {conversation.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="field-label">
              <span>Tutor mode</span>
              <select className="form-control" value={tutorMode} onChange={(event) => setTutorMode(event.target.value)}>
                {tutorModeOptions.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </label>
          </div>

          {tutorMode === "configuration_review" ? (
            <div className="learning-tutor-warning">
              {pmpLesson
                ? "Reasoning review is advisory only. Use it to test your judgment, not as a replacement for reading the scenario carefully."
                : "Configuration review is advisory only. Secrets should not be pasted here, and generated guidance is not tested."}
            </div>
          ) : null}

          <div className="learning-tutor-suggestions">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt.label}
                type="button"
                className="learning-tutor-suggestion"
                onClick={() => {
                  setTutorInput(prompt.text);
                  tutorInputRef.current?.focus();
                }}
              >
                {prompt.label}
              </button>
            ))}
          </div>

          {tutorError ? <div className="feedback-panel error">{tutorError}</div> : null}
          {tutorNotice ? <div className="feedback-panel">{tutorNotice}</div> : null}

          <div className="learning-tutor-history">
            {tutorLoading ? <div className="feedback-panel">Loading AI Tutor...</div> : null}
            {!tutorLoading && !activeConversationId ? (
              <div className="learning-empty-state">
                <strong>No AI Tutor conversation yet.</strong>
                <span>Create a conversation to start asking lesson questions.</span>
              </div>
            ) : null}
            {!tutorLoading && activeConversationId && !tutorMessages.length ? (
              <div className="learning-empty-state">
                <strong>Conversation is empty.</strong>
                <span>Use a suggested prompt or ask a lesson-specific question below.</span>
              </div>
            ) : null}
            {tutorMessages.map((message) => (
              <TutorMessage key={message.id} message={message} />
            ))}
            <div ref={tutorEndRef} />
          </div>

          {latestFailedAssistantMessage && latestUserPrompt ? (
            <div className="learning-tutor-retry-row">
              <button type="button" className="action-button action-button-secondary" onClick={handleRetryFailedMessage} disabled={tutorSending}>
                Retry failed message
              </button>
            </div>
          ) : null}

          <form className="learning-tutor-form" onSubmit={handleTutorSubmit}>
            <label className="field-label">
              <span>Ask the tutor</span>
              <textarea
                ref={tutorInputRef}
                className="form-control learning-tutor-textarea"
                value={tutorInput}
                onChange={(event) => setTutorInput(event.target.value)}
                placeholder={pmpLesson
                  ? "Ask about the lesson, decision flow, artifacts, scenario handling, or quiz feedback."
                  : "Ask about the lesson, packet flow, configuration, troubleshooting, or quiz feedback."}
              />
            </label>
            <div className="learning-tutor-submit-row">
              <button type="submit" className="action-button" disabled={tutorSending || !tutorInput.trim()}>
                {tutorSending ? "Sending..." : "Send"}
              </button>
              <span className="learning-tutor-helper-text">Lesson context is loaded on the server using your authenticated lesson ID.</span>
            </div>
          </form>
        </aside>
      </div>
    </section>
  );
}




